import React from "react";
import styled, { css } from "styled-components";
import { Tag } from "antd";

// Rides on the label it follows rather than sitting beside it. The lift is
// expressed twice on purpose, since callers place this next to text in both
// kinds of container: `vertical-align` handles inline/block parents and is
// ignored by flex items, while `align-self` + the negative block margin handle
// flex parents and are ignored everywhere else.
const superscriptStyles = css`
  vertical-align: super;
  align-self: flex-start;
  margin-block-start: -2px;
  margin-inline-start: 4px;
`;

// Colour comes from the antd Tag preset passed in as `color`, so only geometry
// is set here. `&&&` outranks antd's own .ant-tag rules whatever the stylesheet
// injection order turns out to be.
const BetaTagStyled = styled(Tag)`
  &&& {
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.06em;
    line-height: 1.4;
    border-radius: 20px;
    padding: 0 5px;
    margin-inline-end: 0;
    white-space: nowrap;

    /* Decorative label — never reads as a control, and stays out of selection. */
    pointer-events: none;
    user-select: none;

    ${({ $superscript }) => $superscript && superscriptStyles}
  }
`;

/**
 * SbBetaTag — maturity marker for any feature that isn't GA yet.
 *
 * Renders as a superscript on the label it follows, so put it inside the
 * heading/title element rather than beside it:
 *
 *   <span className="page-title">Reports<SbBetaTag /></span>
 *   <SbBetaTag label="Alpha" color="orange" />
 *   <SbBetaTag superscript={false} />   // plain inline pill
 *
 * `color` takes any antd Tag colour — a preset name for the tinted look, or a
 * hex for a solid fill. See https://ant.design/components/tag
 */
const SbBetaTag = ({
  label = "Beta",
  color = "blue",
  superscript = true,
  children,
  ...rest
}) => (
  <BetaTagStyled
    color={color}
    bordered={false}
    $superscript={superscript}
    {...rest}
  >
    {children ?? label}
  </BetaTagStyled>
);

export default SbBetaTag;
